import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SERVER_URL } from "../../ServerLink";
import AddendumDocument from "../../../components/Addendum/AddendumDocument";
import logo from "../../../assets/images/logo-sm.svg";

/* ─── fetch helper (no axios = no auth interceptors) ────────────────────── */
async function apiFetch(url, options = {}) {
  const res = await fetch(url, {
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok)
    throw Object.assign(new Error(json.message || "Request failed"), { data: json });
  return json;
}

function todayLabel() {
  return new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
}

function composeAddress(a = {}) {
  const unit = String(a.unit_no || "").trim();
  const bld = String(a.building_name || "").trim();
  if (unit || bld) return [unit, bld].filter(Boolean).join(" - ");
  return a.property_address || "";
}

/* ─── Design tokens — Rentdigicare blue only ─────────────────────────────── */
const C = {
  primary:    "#0C447C",
  primaryDk:  "#083460",
  primaryMd:  "#1A6FB5",
  primaryLt:  "#EBF3FC",
  primaryPale:"#F8FAFD",
  accent:     "#3B82F6",   // bright blue highlight
  border:     "#C4D7ED",
  borderLt:   "#DDE9F5",
  surface:    "#FFFFFF",
  bg:         "#FFFFFF",   // page background
  muted:      "#5A7FA8",
  text:       "#0F1F35",
  green:      "#14532D",
  greenBg:    "#F0FDF4",
  greenBorder:"#86EFAC",
  red:        "#991B1B",
  redBg:      "#FEF2F2",
  redBorder:  "#FCA5A5",
};

/* ─── Injected global styles ─────────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');

  .ads-root {
    font-family: 'DM Sans', system-ui, sans-serif;
    background: ${C.bg};
    min-height: 100vh;
    color: ${C.text};
  }
  .ads-root *, .ads-root *::before, .ads-root *::after { box-sizing: border-box; }
  /* Neutralize the global strong{font-size:18px} so bold inherits its context. */
  .ads-root strong, .ads-root b { font-size: inherit; font-weight: 600; color: inherit; line-height: inherit; }

  @keyframes ads-fadeUp {
    from { opacity:0; transform:translateY(14px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes ads-spin { to { transform:rotate(360deg); } }

  .ads-u { animation: ads-fadeUp 0.5s cubic-bezier(.22,.68,0,1.15) both; }
  .ads-d1 { animation-delay:.06s; }
  .ads-d2 { animation-delay:.14s; }
  .ads-d3 { animation-delay:.22s; }
  .ads-d4 { animation-delay:.30s; }

  /* Top nav bar */
  .ads-nav {
    background: ${C.surface};
    border-bottom: 1px solid ${C.borderLt};
    padding: 0 28px;
    position: sticky; top:0; z-index:200;
    box-shadow: 0 1px 4px rgba(12,68,124,.07);
  }
  .ads-nav-inner {
    max-width:880px; margin:0 auto; height:62px;
    display:flex; align-items:center; justify-content:space-between;
  }
  .ads-ref-badge {
    font-size:11px; font-weight:600; letter-spacing:.7px; text-transform:uppercase;
    color:${C.primary}; background:${C.primaryLt}; border:1px solid ${C.border};
    border-radius:5px; padding:4px 11px;
  }

  /* Page wrapper */
  .ads-page { max-width:880px; margin:0 auto; padding:32px 20px 80px; }

  /* Hero card */
  .ads-hero {
    background:${C.surface}; border:1px solid ${C.borderLt}; border-radius:14px;
    overflow:hidden; margin-bottom:18px;
    box-shadow:0 2px 16px rgba(12,68,124,.07);
  }
  .ads-hero-band {
    background:linear-gradient(100deg, ${C.primary} 0%, ${C.primaryMd} 100%);
    padding:26px 32px 22px;
    position:relative; overflow:hidden;
  }
  .ads-hero-band::after {
    content:'';
    position:absolute; right:-60px; top:-60px;
    width:240px; height:240px; border-radius:50%;
    background:rgba(255,255,255,.06); pointer-events:none;
  }
  .ads-hero-band::before {
    content:'';
    position:absolute; right:60px; bottom:-80px;
    width:160px; height:160px; border-radius:50%;
    background:rgba(255,255,255,.04); pointer-events:none;
  }
  .ads-hero-eyebrow {
    font-size:10.5px; font-weight:700; letter-spacing:2.2px; text-transform:uppercase;
    color:rgba(255,255,255,.65); margin-bottom:10px;
    display:flex; align-items:center; gap:7px;
  }
  .ads-hero-name {
    font-family:'Outfit',system-ui,sans-serif;
    font-size:clamp(20px,3.5vw,26px); font-weight:700; color:#fff;
    line-height:1.25; margin-bottom:10px;
  }
  .ads-hero-sub {
    font-size:13.5px; color:rgba(255,255,255,.82); line-height:1.6; max-width:540px;
    margin-bottom:18px;
  }
  .ads-pill {
    display:inline-flex; align-items:center; gap:7px;
    background:rgba(255,255,255,.13); border:1px solid rgba(255,255,255,.22);
    border-radius:6px; padding:6px 13px; font-size:12.5px; color:rgba(255,255,255,.92);
    font-weight:500; backdrop-filter:blur(4px);
  }
  .ads-hero-meta { display:flex; flex-wrap:wrap; gap:8px; }

  .ads-hero-body { padding:20px 32px 22px; }

  /* Stepper */
  .ads-stepper {
    display:flex; align-items:center; margin-bottom:24px;
  }
  .ads-step-dot {
    width:30px; height:30px; border-radius:50%;
    display:flex; align-items:center; justify-content:center;
    font-size:12px; font-weight:700; flex-shrink:0; transition:all .3s;
  }
  .ads-step-dot.active  { background:${C.primary}; color:#fff; box-shadow:0 0 0 4px ${C.primaryLt}; }
  .ads-step-dot.done    { background:${C.primaryMd}; color:#fff; }
  .ads-step-dot.pending { background:${C.borderLt}; color:${C.muted}; }
  .ads-step-label {
    font-size:12px; font-weight:600; letter-spacing:.4px; text-transform:uppercase;
  }
  .ads-step-label.active  { color:${C.primary}; }
  .ads-step-label.done    { color:${C.primaryMd}; }
  .ads-step-label.pending { color:${C.muted}; }
  .ads-step-line { flex:1; height:1px; margin:0 10px; background:${C.borderLt}; transition:background .4s; }
  .ads-step-line.done { background:${C.primaryMd}; }

  /* Info hint */
  .ads-hint {
    display:flex; align-items:flex-start; gap:11px;
    background:${C.primaryPale}; border:1px solid ${C.borderLt};
    border-left:3px solid ${C.primary}; border-radius:0 8px 8px 0;
    padding:13px 18px; font-size:13.5px; color:#3B5F82; line-height:1.58;
    margin-bottom:20px;
  }

  /* Submit button */
  .ads-submit {
    width:100%; padding:16px 0; border:none; border-radius:9px; cursor:pointer;
    font-family:'DM Sans',sans-serif; font-size:15px; font-weight:600; letter-spacing:.3px;
    background:${C.primary}; color:#fff;
    box-shadow:0 4px 18px rgba(12,68,124,.28);
    transition:background .18s, box-shadow .18s, transform .12s;
    position:relative; overflow:hidden;
  }
  .ads-submit::after {
    content:''; position:absolute; inset:0;
    background:linear-gradient(135deg,rgba(255,255,255,.08) 0%,transparent 60%);
    pointer-events:none;
  }
  .ads-submit:hover:not(:disabled) {
    background:${C.primaryDk};
    box-shadow:0 6px 24px rgba(12,68,124,.36);
    transform:translateY(-1px);
  }
  .ads-submit:disabled { background:#94A3B8; box-shadow:none; cursor:not-allowed; transform:none; }

  .ads-placeholder {
    width:100%; padding:15px 0; border-radius:9px;
    border:1.5px dashed ${C.border}; background:${C.primaryPale};
    display:flex; align-items:center; justify-content:center; gap:9px;
    font-size:14px; font-weight:600; color:${C.primary}; letter-spacing:.2px;
  }

  .ads-legal {
    text-align:center; font-size:11.5px; color:#94A3B8; margin-top:14px; line-height:1.65;
  }

  /* Error / success cards */
  .ads-card {
    background:${C.surface}; border:1px solid ${C.borderLt}; border-radius:14px;
    box-shadow:0 2px 16px rgba(12,68,124,.06);
  }

  /* Nav secure badge */
  .ads-secure { display:flex; align-items:center; gap:7px; font-size:13px; font-weight:600; color:#16A34A; }

  /* Hero — two cards */
  .ads-hero-grid { display:flex; gap:18px; margin-bottom:18px; align-items:stretch; }
  .ads-greet {
    flex:1.7 1 0; background:${C.surface}; border:1px solid ${C.borderLt}; border-radius:14px;
    box-shadow:0 2px 16px rgba(12,68,124,.07); padding:26px 30px; display:flex; gap:18px;
  }
  .ads-greet-icon {
    width:46px; height:46px; border-radius:12px; background:${C.primaryLt};
    display:flex; align-items:center; justify-content:center; flex:0 0 auto;
  }
  .ads-greet-eyebrow { font-size:11px; font-weight:700; letter-spacing:1.4px; text-transform:uppercase; color:${C.primaryMd}; margin-bottom:8px; }
  .ads-greet-name { font-family:'Outfit',system-ui,sans-serif; font-size:clamp(22px,3vw,28px); font-weight:700; color:${C.text}; line-height:1.2; margin-bottom:12px; }
  .ads-greet-sub { font-size:14px; color:#4A5C73; line-height:1.65; margin:0 0 18px; max-width:520px; }
  .ads-addr-pill {
    display:inline-flex; align-items:center; gap:8px; background:${C.primaryPale};
    border:1px solid ${C.borderLt}; border-radius:8px; padding:8px 14px; font-size:13.5px; color:${C.text}; font-weight:500;
  }
  .ads-steps {
    flex:1 1 0; background:${C.surface}; border:1px solid ${C.borderLt}; border-radius:14px;
    box-shadow:0 2px 16px rgba(12,68,124,.07); padding:18px 8px; display:flex; align-items:center;
  }
  .ads-step-col { flex:1; text-align:center; padding:6px 12px; }
  .ads-step-col + .ads-step-col { border-left:1px solid ${C.borderLt}; }
  .ads-step-ttl { font-size:14px; font-weight:700; color:${C.text}; margin:8px 0 4px; }
  .ads-step-desc { font-size:11.5px; color:${C.muted}; line-height:1.45; }

  @media (max-width:760px) { .ads-hero-grid { flex-direction:column; } }
`;

/* ─── Inline icons ───────────────────────────────────────────────────────── */
const Ico = {
  doc: (c, s = 22) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  pen: (c, s = 22) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/>
    </svg>
  ),
  check: (c, s = 22) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </svg>
  ),
  pin: (c, s = 15) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
  ),
  shield: (c, s = 15) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 9.5"/>
    </svg>
  ),
  lock: (c, s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  ),
};

/* ─── Shell ──────────────────────────────────────────────────────────────── */
const Shell = ({ children }) => (
  <div className="ads-root">
    <style>{CSS}</style>
    <nav className="ads-nav">
      <div className="ads-nav-inner">
        <img src={logo} alt="Rentdigicare" style={{ height: 36 }} />
        <span className="ads-secure">{Ico.shield("#16A34A")} Secure &amp; legally binding</span>
      </div>
    </nav>
    <div className="ads-page">{children}</div>
  </div>
);

/* ─── Stepper ────────────────────────────────────────────────────────────── */
const Stepper = ({ step }) => {
  const steps = ["Review", "Sign", "Submit"];
  return (
    <div className="ads-stepper">
      {steps.map((label, i) => {
        const s = i + 1 < step ? "done" : i + 1 === step ? "active" : "pending";
        return (
          <React.Fragment key={label}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div className={`ads-step-dot ${s}`}>{s === "done" ? "✓" : i + 1}</div>
              <span className={`ads-step-label ${s}`}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`ads-step-line${s === "done" ? " done" : ""}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ─── Hero (minimal info) ────────────────────────────────────────────────── */
const HeroCard = ({ signerName, addendum }) => {
  const addr = composeAddress(addendum);
  return (
    <div className="ads-card ads-u" style={{ padding: "14px 18px", marginBottom: 18 }}>
      <div className="ads-greet-eyebrow">Electronic Signature Required</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginTop: 4 }}>
        Hello, {signerName || "Valued Tenant"}{addr ? ` · ${addr}` : ""}
      </div>
    </div>
  );
};

/* ─── Main ───────────────────────────────────────────────────────────────── */
const AddendumSign = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [capturedDate, setCapturedDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const json = await apiFetch(`${SERVER_URL}/v2/lease/tenant/addendum/${token}`);
        if (!active) return;
        const data = json?.data;
        setState({ loading: false, error: "", data });
        if (data?.already_signed) setDone(true);
      } catch (e) {
        if (!active) return;
        setState({ loading: false, error: e?.data?.message || e.message || "This link is invalid or has expired.", data: null });
      }
    })();
    return () => { active = false; };
  }, [token]);

  const handleSign = (dataUrl) => {
    setSignature(dataUrl);
    if (dataUrl) setCapturedDate(todayLabel());
  };

  const submit = async () => {
    if (!signature || !agreed) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      await apiFetch(`${SERVER_URL}/v2/lease/tenant/addendum/${token}/sign`, {
        method: "POST",
        body: JSON.stringify({ signature, agreed }),
      });
      setDone(true);
    } catch (e) {
      setSubmitError(e?.data?.message || e.message || "Could not record your signature.");
    } finally {
      setSubmitting(false);
    }
  };

  /* Loading */
  if (state.loading)
    return (
      <Shell>
        <style>{CSS}</style>
        <div style={{ textAlign: "center", padding: "80px 20px" }}>
          <div style={{
            width: 40, height: 40, border: `3px solid ${C.borderLt}`, borderTopColor: C.primary,
            borderRadius: "50%", margin: "0 auto 16px", animation: "ads-spin .8s linear infinite",
          }} />
          <span style={{ fontSize: 14, color: C.muted }}>Loading document…</span>
        </div>
      </Shell>
    );

  /* Error */
  if (state.error && !state.data)
    return (
      <Shell>
        <style>{CSS}</style>
        <div className="ads-card" style={{ maxWidth: 500, margin: "48px auto", padding: "44px 40px", textAlign: "center" }}>
          <div style={{
            width: 58, height: 58, borderRadius: "50%", background: C.redBg,
            border: `2px solid ${C.redBorder}`, display: "flex", alignItems: "center",
            justifyContent: "center", margin: "0 auto 18px", fontSize: 26,
          }}>⚠️</div>
          <h3 style={{ fontFamily: "'Outfit',sans-serif", fontSize: 22, color: C.text, margin: "0 0 10px", fontWeight: 700 }}>
            Link Unavailable
          </h3>
          <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.65, margin: "0 0 8px" }}>{state.error}</p>
          <p style={{ fontSize: 12.5, color: "#9CA3AF", lineHeight: 1.55, margin: 0 }}>
            Please contact your property manager to request a new signing link.
          </p>
        </div>
      </Shell>
    );

  const { addendum, tenant_index, signer_name } = state.data || {};

  /* Done */
  if (done)
    return (
      <Shell>
        <style>{CSS}</style>
        <div className="ads-card ads-u" style={{ maxWidth: 500, margin: "48px auto", overflow: "hidden", textAlign: "center" }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${C.primary}, ${C.primaryMd})` }} />
          <div style={{ padding: "44px 40px" }}>
            <div style={{
              width: 64, height: 64, borderRadius: "50%", background: C.greenBg,
              border: `2px solid ${C.greenBorder}`, display: "flex", alignItems: "center",
              justifyContent: "center", margin: "0 auto 20px", fontSize: 28,
            }}>✓</div>
            <h3 style={{ fontFamily: "'Outfit',sans-serif", fontSize: 26, fontWeight: 700, color: C.text, margin: "0 0 10px" }}>
              Signature Recorded
            </h3>
            <p style={{ fontSize: 15, color: "#374151", lineHeight: 1.65, margin: "0 0 10px" }}>
              Thank you, <strong>{signer_name || "tenant"}</strong>.
            </p>
            <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.65, margin: 0 }}>
              Your signature has been saved.
              Your property manager will finalize the addendum once all parties have signed, and an automated email copy will arrive.
            </p>
          </div>
        </div>
      </Shell>
    );

  const step = signature ? 3 : 2;

  return (
    <Shell>
      <Stepper step={step} />

      <HeroCard signerName={signer_name} addendum={addendum} />

      {!signature && (
        <div className="ads-hint ads-u ads-d1">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.primary} strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 2 }}>
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>
            Read the full document below, tick the agreement checkbox, then scroll to the <strong>Signatures</strong> section.
            Tap your designated signing area to open the signature tool — you can type, draw, or upload your signature.
          </span>
        </div>
      )}

      <div className="ads-card ads-u ads-d2" style={{ padding: "34px 40px" }}>
        <AddendumDocument
          addendum={addendum}
          fluid
          signTenantIndex={tenant_index}
          signatureValue={signature}
          onSign={handleSign}
          capturedDate={capturedDate}
          agreed={agreed}
          onAgreedChange={setAgreed}
        />
      </div>

      {submitError && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          background: C.redBg, border: `1px solid ${C.redBorder}`,
          borderRadius: 8, padding: "12px 16px", marginTop: 16,
          fontSize: 13.5, color: C.red,
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {submitError}
        </div>
      )}

      <div className="ads-u ads-d3" style={{ marginTop: 24, maxWidth: 760, marginLeft: "auto", marginRight: "auto" }}>
        {signature ? (
          <button className="ads-submit" onClick={submit} disabled={submitting || !agreed}>
            {submitting ? "Submitting…" : "Submit signed addendum →"}
          </button>
        ) : (
          <div className="ads-placeholder">
            {Ico.lock(C.primary)} Add your signature above to continue
          </div>
        )}
        <p className="ads-legal">
          By submitting, you acknowledge that your electronic signature constitutes a legally binding signature on this Lease Addendum Agreement.
        </p>
      </div>
    </Shell>
  );
};

export default AddendumSign;
